import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CALENDAR, useI18n } from '../lib/i18n';
import { Select } from './Select';

/**
 * Sana maydoni: qo'lda kiritish + kalendar.
 *
 * Brauzerning `<input type="date">` i har bir OS'da boshqacha ko'rinadi va
 * tug'ilgan sana uchun noqulay — yilni topish uchun o'nlab marta bosish
 * kerak. Shuning uchun o'zimizniki: `kk.oo.yyyy` niqobli maydon, yonida
 * kalendar tugmasi, kalendarda esa oy va yil ro'yxatdan tanlanadi.
 *
 * Tashqi qiymat har doim ISO (`YYYY-MM-DD`) — backend shuni kutadi.
 */

interface DateFieldProps {
  label: string;
  value: string;
  onChange: (iso: string) => void;
  error?: string | undefined;
  /** ISO. Standart: bugun. */
  max?: string;
  /** ISO. Standart: `max` dan 100 yil oldin. */
  min?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth(), now.getDate());
}

/** `1994-03-21` → `21.03.1994`. Noto'g'ri qiymatda bo'sh satr. */
function isoToText(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

/** Kiritilgan raqamlarni `kk.oo.yyyy` ko'rinishiga keltiradi. */
function maskDigits(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('.');
}

/** `21.03.1994` → `1994-03-21`, agar sana haqiqiy bo'lsa. */
function textToIso(text: string): string {
  const d = text.replace(/\D/g, '');
  if (d.length !== 8) return '';

  const day = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const year = Number(d.slice(4, 8));
  if (month < 1 || month > 12 || day < 1) return '';

  const date = new Date(Date.UTC(year, month - 1, day));
  // Mavjud bo'lmagan sana (masalan, 31.02) boshqa oyga «sirg'alib» ketadi.
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';

  return toIso(year, month - 1, day);
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Oyning 1-kuni haftaning nechanchi kuni (0 = dushanba). */
function firstWeekdayIndex(year: number, month: number): number {
  return (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
}

export function DateField({
  label,
  value,
  onChange,
  error,
  max,
  min,
  required,
  disabled,
  className = '',
}: DateFieldProps) {
  const { lang, t } = useI18n();
  const id = useId();
  const wrapRef = useRef<HTMLDivElement>(null);

  const maxIso = max ?? todayIso();
  const minIso = min ?? `${Number(maxIso.slice(0, 4)) - 100}${maxIso.slice(4)}`;

  const [text, setText] = useState(() => isoToText(value));
  const [open, setOpen] = useState(false);

  // Qiymat tashqaridan o'zgarsa (masalan, forma tozalansa) — matnni yangilaymiz.
  // Ataylab faqat `value` ga qaraymiz: `text` ni qo'shsak, yarim kiritilgan
  // sana («21.03.») darrov o'chib ketardi.
  useEffect(() => {
    setText((current) => (textToIso(current) === value ? current : isoToText(value)));
  }, [value]);

  // Kalendar ko'rsatayotgan oy.
  const [view, setView] = useState(() => value || maxIso);
  useEffect(() => {
    if (value) setView(value);
  }, [value]);

  const viewYear = Number(view.slice(0, 4));
  const viewMonth = Number(view.slice(5, 7)) - 1;

  const years = useMemo(() => {
    const from = Number(minIso.slice(0, 4));
    const to = Number(maxIso.slice(0, 4));
    return Array.from({ length: to - from + 1 }, (_, i) => to - i);
  }, [minIso, maxIso]);

  // Tashqariga bosilsa yoki Esc — kalendar yopiladi.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function handleText(next: string) {
    const masked = maskDigits(next);
    setText(masked);
    onChange(textToIso(masked));
  }

  function shiftMonth(delta: number) {
    const date = new Date(Date.UTC(viewYear, viewMonth + delta, 1));
    setView(toIso(date.getUTCFullYear(), date.getUTCMonth(), 1));
  }

  function pick(day: number) {
    const iso = toIso(viewYear, viewMonth, day);
    setText(isoToText(iso));
    onChange(iso);
    setOpen(false);
  }

  const { months, weekdays } = CALENDAR[lang];
  const total = daysInMonth(viewYear, viewMonth);
  const offset = firstWeekdayIndex(viewYear, viewMonth);
  const minTime = Date.parse(minIso);
  const maxTime = Date.parse(maxIso);
  // Chegaradan chiqqan sana ko'rinadi, lekin bosilmaydi.
  const outOfRange = (day: number) => {
    const time = Date.UTC(viewYear, viewMonth, day);
    return time < minTime || time > maxTime;
  };

  const invalid = text.replace(/\D/g, '').length === 8 && !value;

  return (
    <div className={`field field--date ${className}`}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>

      <div className="datefield" ref={wrapRef}>
        <input
          id={id}
          className="field__input datefield__input"
          inputMode="numeric"
          autoComplete="off"
          placeholder={t('date.placeholder')}
          value={text}
          onChange={(e) => handleText(e.target.value)}
          onFocus={() => setOpen(false)}
          disabled={disabled}
          required={required}
          aria-label={label}
          aria-invalid={invalid || Boolean(error)}
        />
        <button
          type="button"
          className="datefield__toggle"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-label={t('date.open')}
          aria-expanded={open}
        >
          <svg width="17" height="17" viewBox="0 0 20 20" aria-hidden="true">
            <rect x="2.5" y="4" width="15" height="13.5" rx="2.5" fill="none"
              stroke="currentColor" strokeWidth="1.5" />
            <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" fill="none"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {open && (
          <div className="calendar" role="dialog" aria-label={label}>
            <div className="calendar__head">
              <button
                type="button"
                className="calendar__nav"
                onClick={() => shiftMonth(-1)}
                aria-label={t('date.prevMonth')}
              >
                ‹
              </button>

              <Select
                className="calendar__select"
                value={String(viewMonth)}
                options={months.map((name, index) => ({ value: String(index), label: name }))}
                onChange={(month) => setView(toIso(viewYear, Number(month), 1))}
                ariaLabel={t('date.month')}
              />

              <Select
                className="calendar__select calendar__select--year"
                value={String(viewYear)}
                options={years.map((year) => ({ value: String(year), label: String(year) }))}
                onChange={(year) => setView(toIso(Number(year), viewMonth, 1))}
                ariaLabel={t('date.year')}
              />

              <button
                type="button"
                className="calendar__nav"
                onClick={() => shiftMonth(1)}
                aria-label={t('date.nextMonth')}
              >
                ›
              </button>
            </div>

            <div className="calendar__weekdays" aria-hidden="true">
              {weekdays.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="calendar__grid">
              {Array.from({ length: offset }, (_, i) => (
                <span key={`pad-${i}`} />
              ))}
              {Array.from({ length: total }, (_, i) => i + 1).map((day) => {
                const iso = toIso(viewYear, viewMonth, day);
                return (
                  <button
                    key={day}
                    type="button"
                    className={`calendar__day ${iso === value ? 'is-selected' : ''}`}
                    disabled={outOfRange(day)}
                    onClick={() => pick(day)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            <div className="calendar__foot">
              <button
                type="button"
                className="btn btn--small"
                onClick={() => {
                  setText('');
                  onChange('');
                  setOpen(false);
                }}
              >
                {t('date.clear')}
              </button>
            </div>
          </div>
        )}
      </div>

      {invalid && !error && <span className="field__error">{t('date.invalid')}</span>}
      {error && <span className="field__error">{error}</span>}
    </div>
  );
}
